import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

export default function QuoteAcceptedDialog({ open, onClose, kind, entity, estimatedAmount, onSaved }) {
  const [amount, setAmount] = useState(estimatedAmount ?? "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  if (!entity) return null;

  const path = kind === "intervention" ? `/interventions/${entity.id}/quote-accepted` : `/chantiers/${entity.id}/quote-accepted`;
  const label = kind === "intervention" ? entity.title : entity.name;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(path, {
        quote_accepted: true,
        accepted_amount: amount === "" ? null : Number(amount),
        accepted_note: note || null,
      });
      toast.success("Devis marqué comme accepté — BKpay & webhooks notifiés");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle size={20} weight="duotone" className="text-emerald-400" />
            Devis accepté — {label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200 flex gap-2">
            <ArrowsClockwise size={14} weight="fill" className="shrink-0 mt-0.5" />
            <div>
              L&apos;acceptation va déclencher un envoi automatique vers <b>BKpay</b> (si activé) et vers tous les <b>webhooks</b> abonnés à l&apos;événement <code className="text-white/70">quote.accepted</code> — Sage, Pennylane, EBP, Zapier, n8n, etc.
            </div>
          </div>
          <div>
            <Label className="text-xs">Montant accepté (€ HT)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={estimatedAmount ? `Estimé : ${estimatedAmount}` : "Optionnel"} className="bg-white/5 border-white/10" data-testid="quote-accepted-amount" />
          </div>
          <div>
            <Label className="text-xs">Note (optionnel)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Devis signé le 04/08, à facturer sous 30j…" className="bg-white/5 border-white/10 text-sm" data-testid="quote-accepted-note" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="quote-accepted-save">
            <CheckCircle size={16} /> {saving ? "Envoi…" : "Confirmer l'acceptation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
